import { useGetList } from "ra-core";
import { useMemo } from "react";

import { compareByDate } from "@/lib/safeDate";

import { nullsLastMeta } from "../providers/commons/nullsLast";
import type { Contact, ContactNote, Task } from "../types";
import { AANDACHT_LIMIT, VANDAAG_PREVIEW_LIMIT } from "./constants";
import { dueBucket, isOverdueOrToday } from "./dates";

export type ActionRow = {
  task: Task;
  contact: Contact;
  latestNote?: ContactNote;
};

const listOptions = { retry: false };

export const useOpenActions = () => {
  const tasksQuery = useGetList<Task>(
    "tasks",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "due_date", order: "ASC" },
      filter: { "done_date@is": null },
      meta: nullsLastMeta,
    },
    listOptions,
  );
  const contactsQuery = useGetList<Contact>(
    "contacts",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "id", order: "ASC" },
    },
    listOptions,
  );
  const notesQuery = useGetList<ContactNote>(
    "contact_notes",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "date", order: "DESC" },
      meta: nullsLastMeta,
    },
    listOptions,
  );
  const rows = useMemo<ActionRow[]>(() => {
    const tasks = tasksQuery.data ?? [];
    const contacts = contactsQuery.data ?? [];
    const notes = [...(notesQuery.data ?? [])].sort((a, b) =>
      compareByDate(a.date, b.date),
    );
    const contactById = new Map(
      contacts.map((contact) => [contact.id, contact]),
    );
    const latestByContact = new Map<Contact["id"], ContactNote>();
    for (const note of notes) {
      if (!latestByContact.has(note.contact_id)) {
        latestByContact.set(note.contact_id, note);
      }
    }
    return tasks.flatMap((task) => {
      // The cached list is patched in place and keeps a finished task until
      // the refetch. Open rows are only tasks that are still open.
      if (task.done_date != null) return [];
      const contact = contactById.get(task.contact_id);
      if (!contact) return [];
      return [
        {
          task,
          contact,
          latestNote: latestByContact.get(contact.id),
        },
      ];
    });
  }, [contactsQuery.data, notesQuery.data, tasksQuery.data]);

  return {
    rows,
    isPending: tasksQuery.isPending || contactsQuery.isPending,
    // Notes only decorate a row. A failed note fetch must not hide tasks
    // that loaded, and a failed task or contact fetch must not look empty.
    queries: [tasksQuery, contactsQuery],
  };
};

export const useAandachtRows = () => {
  const { rows, isPending, queries } = useOpenActions();
  return {
    rows: rows.slice(0, AANDACHT_LIMIT),
    isPending,
    queries,
  };
};

export const useVandaagRows = () => {
  const { rows, isPending, queries } = useOpenActions();
  const overdue = rows.filter(
    (row) => dueBucket(row.task.due_date) === "overdue",
  );
  const today = rows.filter((row) => dueBucket(row.task.due_date) === "today");
  return {
    overdue,
    today,
    preview: rows
      .filter((row) => isOverdueOrToday(row.task.due_date))
      .filter((row) => dueBucket(row.task.due_date) === "today")
      .slice(0, VANDAAG_PREVIEW_LIMIT),
    isPending,
    queries,
  };
};
